// AlertConfigModal.tsx
//
// O modal que a aba "Alertas e Notificações" chama em
// `handleOpenModal("alertConfig")` desde sempre — em três botões diferentes — e
// que nunca existiu: o shell caía no `default` do switch de modais e nada abria.
//
// ONDE ISTO GRAVA: `acd."ActSubsidiaryInfo"`, `subsidiaryInfoType` 23, uma linha
// por alerta configurado, por loja.
//
// O NÚMERO, e por que não é 21 nem 22: a faixa nossa começa em 20 (a baixa é do
// legado e significa coisas diferentes em acd e aod), e 20, 21 e 22 JÁ ESTÃO
// OCUPADOS — meiosDePagamento, coordenada e contato, ver
// `alfredo-central-backend/src/common/subsidiaryInfo.enum.ts`, arquivo que
// existe idêntico no OPS. Gravar alerta em 21 ou 22 escreveria um agregado por
// cima de outro: a filial perderia a coordenada ou o telefone, e o estrago só
// apareceria quando o cliente fosse mandado para a unidade errada. O primeiro
// número livre é o 23.
//
// O QUE AINDA NÃO EXISTE, e por isso está escrito na tela: o backend expõe
// exatamente UMA rota de escrita em subsidiaryInfo —
// `PATCH /subsidiaryInfo/:subsidiaryUuid/pagamento` (subsidiary.routes.ts) — e
// ela é do tipo 20. Não há rota que grave o tipo 23. Enquanto não houver, este
// formulário abre, valida e RECUSA o salvamento nomeando a rota que falta, sem
// mandar requisição nenhuma — em vez de fechar fingindo que gravou.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

/** Ver o comentário do topo: 20/21/22 ocupados, 23 é o primeiro livre. */
export const SUBSIDIARY_INFO_ALERTA = 23;

/** O que dispara o alerta. Chave gravada; rótulo só de tela. */
export const EVENTOS_DE_ALERTA = [
  { chave: "estoqueBaixo", rotulo: "Estoque abaixo do ponto de reposição" },
  { chave: "rupturaDeEstoque", rotulo: "Ruptura de estoque (chegou a zero)" },
  { chave: "validadeProxima", rotulo: "Produto próximo do vencimento" },
  { chave: "ordemDeCompraGerada", rotulo: "Ordem de compra gerada" },
  { chave: "recebimentoDivergente", rotulo: "Recebimento divergente do pedido" },
] as const;

export const SEVERIDADES = [
  { chave: "baixa", rotulo: "Baixa" },
  { chave: "media", rotulo: "Média" },
  { chave: "alta", rotulo: "Alta" },
  { chave: "critica", rotulo: "Crítica" },
] as const;

/**
 * Por onde o alerta sai.
 *
 * É o MESMO enum que DynamicTable já usa na coluna "Tipo Alerta" dos Limites de
 * Reposição (`ALERT_TYPE_MAP`: email | whatsapp | both | none). O mock que esta
 * tela tinha guardava `channels: ["email", "whatsapp"]`, uma lista de strings
 * livre — dois vocabulários para a mesma pergunta, e nenhum dos dois com quem
 * conversar. Fica o que já está gravado em banco.
 */
export const ALERT_TYPES = [
  { chave: "email", rotulo: "Email" },
  { chave: "whatsapp", rotulo: "WhatsApp" },
  { chave: "both", rotulo: "Ambos" },
  { chave: "none", rotulo: "Nenhum" },
] as const;

export const ROTULO_EVENTO: Record<string, string> = Object.fromEntries(
  EVENTOS_DE_ALERTA.map((evento) => [evento.chave, evento.rotulo])
);

export const ROTULO_SEVERIDADE: Record<string, string> = Object.fromEntries(
  SEVERIDADES.map((severidade) => [severidade.chave, severidade.rotulo])
);

export const ROTULO_ALERT_TYPE: Record<string, string> = Object.fromEntries(
  ALERT_TYPES.map((tipo) => [tipo.chave, tipo.rotulo])
);

export interface ConfigDeAlerta {
  /** uuid da linha em ActSubsidiaryInfo. Ausente = ainda não gravado. */
  subsidiaryInfoUuid?: string;
  subsidiaryUuid: string;
  subsidiaryName?: string;
  nome: string;
  descricao: string;
  evento: string;
  severidade: string;
  alertType: string;
  ativo: boolean;
}

/** Loja, no formato mínimo que o select precisa. */
export interface LojaDoToken {
  subsidiaryUuid: string;
  subsidiaryName: string;
  companyName?: string;
}

/**
 * As lojas do infoToken.
 *
 * Travessia organization -> companies -> subsidiaries, igual a MeiosPagamento.
 * Não sai HTTP daqui: `GET /subsidiaries` não existe (o router registra
 * `/subsidiary/:uuid`) e o token já traz a árvore inteira.
 */
export function lerLojas(decryptedInfoToken: any): LojaDoToken[] {
  const empresas: any[] = decryptedInfoToken?.organization?.companies ?? [];
  return empresas.flatMap((empresa: any) =>
    (empresa?.subsidiaries ?? []).map((filial: any) => ({
      subsidiaryUuid: filial?.subsidiaryUuid,
      subsidiaryName: filial?.subsidiaryName,
      companyName: empresa?.companyName,
    }))
  );
}

/**
 * Os alertas GRAVADOS, lidos do infoToken (tipo 23, dentro de cada filial).
 *
 * A leitura é pela árvore, e não pelo `decryptedInfoToken.subsidiaryInfo` de
 * primeiro nível, por um motivo prático: pela árvore se sabe de QUAL loja é cada
 * linha sem depender de nenhum campo dentro do JSON. Linha com JSON quebrado é
 * pulada — uma linha ruim não pode branquear a aba inteira.
 */
export function lerConfiguracoesDeAlerta(
  decryptedInfoToken: any
): ConfigDeAlerta[] {
  const empresas: any[] = decryptedInfoToken?.organization?.companies ?? [];

  return empresas.flatMap((empresa: any) =>
    (empresa?.subsidiaries ?? []).flatMap((filial: any) =>
      (filial?.subsidiaryInfo ?? [])
        .filter(
          (info: any) =>
            Number(info?.subsidiaryInfoType) === SUBSIDIARY_INFO_ALERTA
        )
        .map((info: any): ConfigDeAlerta | null => {
          let valor: any = {};
          try {
            valor = JSON.parse(info.subsidiaryInfoValue || "{}");
          } catch {
            return null;
          }
          return {
            subsidiaryInfoUuid: info.subsidiaryInfoUuid,
            subsidiaryUuid: filial.subsidiaryUuid,
            subsidiaryName: filial.subsidiaryName,
            nome: valor?.nome || info.subsidiaryInfoName || "(sem nome)",
            descricao: valor?.descricao || "",
            evento: valor?.evento || "",
            severidade: valor?.severidade || "media",
            alertType: valor?.alertType || "none",
            ativo: valor?.ativo === true,
          };
        })
        .filter((config: ConfigDeAlerta | null) => config !== null)
    )
  ) as ConfigDeAlerta[];
}

/** A frase que nomeia a rota que falta. Usada na recusa e no aviso fixo. */
const FALTA_A_ROTA =
  "Não existe rota que grave ActSubsidiaryInfo tipo 23: o backend expõe só " +
  "PATCH /subsidiaryInfo/:subsidiaryUuid/pagamento, que é do tipo 20 (meios de " +
  "pagamento). Nada foi enviado ao servidor.";

interface AlertConfigModalProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

const AlertConfigModal: React.FC<AlertConfigModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-alertconfigmodal",
  });

  const [form, setForm] = useState({
    nome: "",
    descricao: "",
    evento: "",
    severidade: "media",
    alertType: "email",
    subsidiaryUuid: "",
    ativo: true,
  });
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  // O shell fecha o modal quando o salvamento dá certo; sem esta trava o
  // `setSalvando(false)` do `finally` cairia num componente desmontado.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const lojas = useMemo(
    () => lerLojas(decryptedInfoToken),
    [decryptedInfoToken]
  );

  useEffect(() => {
    // O objeto chega de dois jeitos: já parseado pela aba (é o que
    // `lerConfiguracoesDeAlerta` devolve) ou cru do token, com o JSON dentro de
    // `subsidiaryInfoValue`. Ler os dois é o que evita abrir o formulário vazio
    // em cima de uma linha que existe — e gravar o vazio por cima dela.
    const doValorCru = selectedObject?.subsidiaryInfoValue
      ? parseInfoValue(selectedObject.subsidiaryInfoValue)
      : {};

    setForm({
      nome: selectedObject?.nome ?? doValorCru?.nome ?? "",
      descricao: selectedObject?.descricao ?? doValorCru?.descricao ?? "",
      evento: selectedObject?.evento ?? doValorCru?.evento ?? "",
      severidade:
        selectedObject?.severidade ?? doValorCru?.severidade ?? "media",
      alertType: selectedObject?.alertType ?? doValorCru?.alertType ?? "email",
      subsidiaryUuid: selectedObject?.subsidiaryUuid ?? "",
      // `?? true` seria mentira na edição de um alerta desligado: `undefined`
      // só acontece quando não há objeto nenhum, e aí o padrão é ligado.
      ativo:
        selectedObject?.ativo ??
        doValorCru?.ativo ??
        !selectedObject?.subsidiaryInfoUuid,
    });
    setAviso(null);
    // A dependência é SÓ o `selectedObject`. `parseInfoValue` é recriado a cada
    // render de Configuration (`index.tsx`, função solta no corpo do
    // componente), e `handleSave` termina chamando `updateState({ isEditing:
    // false })` — ou seja, todo salvamento re-renderiza o pai e troca a
    // identidade da função. Com ela na lista, este efeito rodava logo DEPOIS de
    // uma recusa e fazia as duas coisas que o modal existe para evitar: apagava
    // o que o usuário tinha digitado e limpava o aviso da recusa antes de ele
    // ser lido. O que o efeito precisa observar é a troca de objeto editado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObject]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const handleSubmit = async () => {
    if (!form.nome.trim()) {
      setAviso({ tom: "erro", texto: "Dê um nome ao alerta." });
      return;
    }
    if (!form.evento) {
      setAviso({
        tom: "erro",
        texto: "Escolha o evento que dispara o alerta.",
      });
      return;
    }
    if (!form.subsidiaryUuid) {
      setAviso({
        tom: "erro",
        texto:
          "Escolha a loja. O alerta é gravado na filial (ActSubsidiaryInfo), " +
          "não na organização — sem loja não há onde gravar.",
      });
      return;
    }

    setSalvando(true);
    try {
      const resultado = await handleSave("alertConfig", {
        subsidiaryInfoUuid: selectedObject?.subsidiaryInfoUuid,
        subsidiaryInfoType: SUBSIDIARY_INFO_ALERTA,
        ...form,
        nome: form.nome.trim(),
      });

      if (resultado && !resultado.ok && montado.current) {
        // O texto vem do shell e é mostrado COMO ELE VEIO. Concatenar
        // `FALTA_A_ROTA` aqui repetia pela terceira vez o que já está no aviso
        // fixo do topo e na recusa do `handleSave` — e, no dia em que a rota
        // existir, transformaria "erro de rede" ou "campo inválido" numa
        // afirmação falsa sobre o backend.
        setAviso({ tom: resultado.tom ?? "erro", texto: resultado.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  const ehEdicao = Boolean(selectedObject?.subsidiaryInfoUuid);

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} medium>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-alertconfigmodal">
            <AlfredoStyles.MaterialIcons>warning</AlfredoStyles.MaterialIcons>
            {/* `{}` é truthy: o useModal faz `setSelectedObject(object || {})`,
                então testar `selectedObject` sozinho diria "Editar" ao criar.
                Quem responde "isto é edição?" é o uuid. */}
            {ehEdicao ? "Editar Alerta" : "Novo Alerta"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            {/* O estado da gravação, dito antes de o usuário digitar — não
                depois de ele clicar em Salvar e perder o que escreveu. */}
            <AlfredoStyles.AlertMessage $tom="atencao">
              <strong>Este formulário ainda não grava.</strong> {FALTA_A_ROTA} O
              que está aqui é o desenho do alerta (tipo 23), pronto para quando a
              rota existir.
            </AlfredoStyles.AlertMessage>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Identificação
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Nome do alerta *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.nome}
                    onChange={(e) => handleLocalChange("nome", e.target.value)}
                    placeholder="Ex: Estoque baixo na Pompeia"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Loja *</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.subsidiaryUuid}
                    onChange={(e) =>
                      handleLocalChange("subsidiaryUuid", e.target.value)
                    }
                  >
                    <option value="">Selecione a loja</option>
                    {lojas.map((loja) => (
                      <option
                        key={loja.subsidiaryUuid}
                        value={loja.subsidiaryUuid}
                      >
                        {loja.companyName
                          ? `${loja.subsidiaryName} — ${loja.companyName}`
                          : loja.subsidiaryName}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label>Descrição</AlfredoStyles.Label>
                  <AlfredoStyles.TextArea
                    value={form.descricao}
                    onChange={(e) =>
                      handleLocalChange("descricao", e.target.value)
                    }
                    placeholder="O que este alerta avisa, e para quem"
                    rows={3}
                  />
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Disparo e envio
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Evento *</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.evento}
                    onChange={(e) => handleLocalChange("evento", e.target.value)}
                  >
                    <option value="">Selecione o evento</option>
                    {EVENTOS_DE_ALERTA.map((evento) => (
                      <option key={evento.chave} value={evento.chave}>
                        {evento.rotulo}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Severidade</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.severidade}
                    onChange={(e) =>
                      handleLocalChange("severidade", e.target.value)
                    }
                  >
                    {SEVERIDADES.map((severidade) => (
                      <option key={severidade.chave} value={severidade.chave}>
                        {severidade.rotulo}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Por onde avisar</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.alertType}
                    onChange={(e) =>
                      handleLocalChange("alertType", e.target.value)
                    }
                  >
                    {ALERT_TYPES.map((tipo) => (
                      <option key={tipo.chave} value={tipo.chave}>
                        {tipo.rotulo}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) =>
                      handleLocalChange("ativo", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel active={form.ativo}>
                  Alerta ativo
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              {form.ativo && form.alertType === "none" && (
                <AlfredoStyles.AlertMessage $tom="atencao">
                  Com "Nenhum" o alerta fica ativo mas não avisa ninguém: ele só
                  ficaria registrado. Escolha Email, WhatsApp ou Ambos para que
                  alguém receba.
                </AlfredoStyles.AlertMessage>
              )}
            </AlfredoStyles.FormSection>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando…"
                : ehEdicao
                ? "Atualizar Alerta"
                : "Criar Alerta"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default AlertConfigModal;
